import { z } from 'zod'

import { SEARCH_MAX_LENGTH } from '@/features/savings/account-filters'
import {
  SAVINGS_ACCOUNT_STATUSES,
  SAVINGS_PRODUCT_CLASSES,
} from '@/features/savings/constants'

/**
 * The savings screens keep their state in the URL, for the same reason the customer and group
 * screens do: a filtered list is a place. "Which security deposits at Kaneshie are dormant?" should
 * be a link somebody can send, and pressing Back out of an account should land on the page of the
 * list it was opened from.
 *
 * Every value is read back through `.catch()`, so a hand-edited or stale URL degrades to the
 * default rather than throwing an operator out of a screen they were working in.
 *
 * These schemas are exported for the router to attach as `validateSearch`; the screens also parse
 * through them directly, which is what lets them read typed values before the routes are wired.
 */

export const DEFAULT_PAGE_SIZE = 25

export const savingsAccountListSearchSchema = z.object({
  // The server refuses a term over 32 characters with `savings.search_too_long`, so a hand-edited
  // or stale URL carrying a longer one degrades to no filter rather than to a 400 the operator
  // cannot clear from the box.
  search: z.string().trim().min(1).max(SEARCH_MAX_LENGTH).optional().catch(undefined),
  status: z.enum(SAVINGS_ACCOUNT_STATUSES).optional().catch(undefined),
  productClass: z.enum(SAVINGS_PRODUCT_CLASSES).optional().catch(undefined),
  branchId: z.string().min(1).optional().catch(undefined),
  customerId: z.string().min(1).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type SavingsAccountListSearch = z.infer<typeof savingsAccountListSearchSchema>

export const SAVINGS_ACCOUNT_TABS = ['overview', 'statement', 'holds'] as const

export type SavingsAccountTab = (typeof SAVINGS_ACCOUNT_TABS)[number]

export const savingsAccountDetailSearchSchema = z.object({
  tab: z.enum(SAVINGS_ACCOUNT_TABS).optional().catch(undefined),
})

export type SavingsAccountDetailSearch = z.infer<typeof savingsAccountDetailSearchSchema>

/**
 * The four cash forms. Which one is open is in the URL because a teller who has just been sent to
 * this screen from an account should land on the right tab, and because reloading mid-shift should
 * not silently move them from a security-deposit top-up to an ordinary deposit.
 */
export const SAVINGS_CASH_TABS = ['deposit', 'withdrawal', 'transfer', 'security-deposit'] as const

export type SavingsCashTab = (typeof SAVINGS_CASH_TABS)[number]

export const savingsCashSearchSchema = z.object({
  tab: z.enum(SAVINGS_CASH_TABS).optional().catch(undefined),
})

export const SAVINGS_CUSTOMER_TABS = ['accounts', 'passbooks', 'prerequisites'] as const

export type SavingsCustomerTab = (typeof SAVINGS_CUSTOMER_TABS)[number]

export const savingsCustomerSearchSchema = z.object({
  tab: z.enum(SAVINGS_CUSTOMER_TABS).optional().catch(undefined),
})

export const savingsProductListSearchSchema = z.object({
  productClass: z.enum(SAVINGS_PRODUCT_CLASSES).optional().catch(undefined),
  includeInactive: z.boolean().optional().catch(undefined),
})

export type SavingsProductListSearch = z.infer<typeof savingsProductListSearchSchema>
